import { useEffect, useRef } from 'react';
import { tinctApi } from '@tinct/core-api';
import { useUserIdFromToken } from '@tinct/core-store-oauth';
import { goToPortalLogout } from './portal-logout';

/** How long the sign-out waits for the API before leaving anyway: the portal logout must never hang. */
export const END_SESSION_TIMEOUT_MS = 3000;

/**
 * First step of the sign-out. Ends this sign-in on the API first (B-275): the authorization behind
 * the token is deleted, so the access token and the refresh token stop working at once instead of
 * at their expiry, and a copy of the token taken before the sign-out is worthless. The person's
 * other devices are untouched. Then the portal's `/logout` ends its session and comes back to
 * `/logout/callback`, which clears this app's stores.
 *
 * Best effort: a failure (network, a token the API already refuses) or a slow answer never keeps
 * the person here.
 */
export function LogoutPage() {
  const hasRedirected = useRef(false);
  const userId = useUserIdFromToken();

  useEffect(() => {
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    // No token, or none naming its user: nothing to end on the API
    if (!userId) {
      goToPortalLogout();
      return;
    }
    // The axios timeout only bounds the request: a token refresh the client runs before sending it,
    // or on a 401, is not covered. So the whole call races a timer of its own.
    const giveUp = new Promise<void>((resolve) => setTimeout(resolve, END_SESSION_TIMEOUT_MS));
    Promise.race([tinctApi.users.endCurrentSession(userId, { timeout: END_SESSION_TIMEOUT_MS }), giveUp])
      .catch((error: unknown) => console.warn('Ending the session on the API failed', error))
      .finally(goToPortalLogout);
  }, [userId]);

  return null;
}

export default LogoutPage;
